import React, { useEffect } from "react";
import clsx from "clsx";
import { Grid } from "@mui/material";
import { Button, Card, SelectField, MenuItem } from "@curves/ui-base";
import CheckoutContent from "@icoach/accountBook/createOrder/components/CheckoutContent";
import ReceiverFieldRenderer from "@icoach/accountBook/createOrder/components/ReceiverFieldRenderer";
import { PageLayout } from "@icoach/components";
import { RECEIVER_TYPE } from "@icoach/constants/dataEnums";
import { ensureArray, isEqual, getErrorModuleStatus } from "@util/utils";
import useReceiverConfirmation from "@icoach/accountBook/createOrder/hooks/useReceiverConfirmation";
import { RECEIVER_LAYOUT_CONFIG } from "@icoach/accountBook/createOrder/CreateOrderPage.util";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import useOptions from "@util/hook/useOptions";
import useMemberCheckoutInfo from "@icoach/accountBook/createOrder/hooks/useMemberCheckoutInfo";
import useOrderCheckoutSubmit from "@icoach/accountBook/createOrder/hooks/useOrderCheckoutSubmit";
import useNavigationGuard from "@util/hook/useNavigationGuard";
import useMessageDialog from "@util/hook/useMessageDialog";

/**
 * 新增收入頁面
 */
const CreateOrderPage = () => {
	// 取得結帳選項 API
	const { getOrderCheckoutOptionsApi } = useOrderCheckoutApi();
	const { options } = useOptions({ getOptionsApi: getOrderCheckoutOptionsApi, isFetchOnMount: true });

	// 取得會員結帳資訊相關的資料和方法
	const { fetchCheckoutInfo, checkoutInfo, clearCheckoutInfo } = useMemberCheckoutInfo();

	// 接收者邏輯核心邏輯
	const { receiverType, isReceiverConfirmed, resetKey, receiverTypeRef, receiverFieldRef, handleReceiverChange, onReceiverConfirm, handleReset } = useReceiverConfirmation({
		onReset: clearCheckoutInfo,
	});

	// 表單提交 hook（在父層管理，以便傳遞 validationErrors 給所有需要錯誤資訊的組件）
	const setMessageDialog = useMessageDialog();
	const { navigationKey, confirmNavigation, cancelNavigation, disableGuard } = useNavigationGuard({
		isGuardEnabled: receiverType !== 0,
	});
	const { submit, validationErrors, clearValidationErrors } = useOrderCheckoutSubmit({ onBeforeNavigate: disableGuard });

	// 導航守衛：選了對象後，使用者嘗試離開時彈出確認 dialog
	useEffect(
		() => {
			if (!navigationKey) return;
			setMessageDialog({
				open: true,
				title: '確定要離開？',
				msg: '此動作將會遺失資料，確定要離開？',
				onAsyncConfirm: async (e, onClose) => {
					await confirmNavigation();
					onClose();
				},
				onClose: cancelNavigation,
			});
		},
		// eslint-disable-next-line
		[navigationKey],
	);

	// 是否顯示結帳內容
	const isShowContent = isReceiverConfirmed;

	// style 配置
	const isOtherClubMember = isEqual(receiverType, RECEIVER_TYPE.CROSSMEMBER);
	const layoutConfig = RECEIVER_LAYOUT_CONFIG[receiverType] || {};

	// 處理設定/重設按鈕點擊
	const handleButtonClick = () => {
		if (!isReceiverConfirmed) {
			// 確認
			onReceiverConfirm(fetchCheckoutInfo);
		} else {
			// 重設
			handleReset();
		}
	};

	return (
		<PageLayout>
			<PageLayout.Header title="新增收入" />
			{/* 訂單基本資訊 */}
			<React.Fragment key={resetKey}>
				{/* 設定對象 */}
				<Card className={clsx("mb-2")}>
					<Card.CardContent className="px-4 pt-2 pb-3">
						<Grid spacing={2} container>
							<Grid xs={3} item>
								<SelectField
									label="設定對象"
									ref={receiverTypeRef}
									onChange={handleReceiverChange}
									fullWidth
									disabled={isReceiverConfirmed}
									required
									{...getErrorModuleStatus(validationErrors, "receiverType")}
								>
									{ensureArray(options?.receiverOptions).map(item => (
										<MenuItem key={item.value} value={item.value}>
											{item.text}
										</MenuItem>
									))}
								</SelectField>
							</Grid>
							<ReceiverFieldRenderer receiverType={receiverType} fieldRef={receiverFieldRef} resource={options} readOnly={isReceiverConfirmed} errorModule={validationErrors} />
							<Grid
								className={clsx({
									"ml-auto": layoutConfig.buttonAlign === "ml-auto" || isOtherClubMember,
									"mt-4": !isOtherClubMember,
								})}
								xs={1}
								item
							>
								<Button className="white-space-nowrap mb-1" variant="contained" onClick={handleButtonClick}>
									{isReceiverConfirmed ? "重設" : "設定"}
								</Button>
							</Grid>
						</Grid>
					</Card.CardContent>
				</Card>
				{isShowContent && (
					<CheckoutContent
						receiverTypeRef={receiverTypeRef}
						receiverFieldRef={receiverFieldRef}
						receiverType={receiverType}
						checkoutInfo={checkoutInfo}
						options={options}
						submit={submit}
						validationErrors={validationErrors}
						clearValidationErrors={clearValidationErrors}
					/>
				)}
			</React.Fragment>
		</PageLayout>
	);
};

export default CreateOrderPage;
